/**
 * Keep the Clarity Brand Agent inside narrow viewports.
 * The vendor renders a 480px panel and its launcher inside an open ShadowRoot.
 * These scoped overrides keep the panel on screen, place the launcher just
 * above the language switcher on every screen and, on phones, keep it a bare
 * circle (no suggested questions). Recheck the floating-view and entrypoint
 * selectors after a vendor widget update.
 */
(() => {
  'use strict';

  const hostId = 'ads-agent-host';
  const styleId = 'rc-clarity-mobile-style';
  const mobileStyles = `
    @media (max-width: 749px) {
      /* The vendor sets panel dimensions and margins inline. */
      [data-testid="floating-view"] {
        --rc-clarity-gap-top: max(8px, env(safe-area-inset-top, 0px));
        --rc-clarity-gap-bottom: max(8px, env(safe-area-inset-bottom, 0px));
        position: fixed !important;
        inset-inline: 8px !important;
        top: calc(var(--rc-clarity-viewport-top, 0px) + var(--rc-clarity-gap-top)) !important;
        bottom: auto !important;
        width: auto !important;
        min-width: 0 !important;
        max-width: none !important;
        height: min(720px, calc(var(--rc-clarity-viewport-height, 100dvh) - var(--rc-clarity-gap-top) - var(--rc-clarity-gap-bottom))) !important;
        max-height: calc(var(--rc-clarity-viewport-height, 100dvh) - var(--rc-clarity-gap-top) - var(--rc-clarity-gap-bottom)) !important;
        margin: 0 !important;
        border-radius: 8px !important;
      }

      [data-testid="floating-view"] > div:first-child > div:first-child {
        min-width: 0;
        gap: 8px;
        padding: 12px;
      }

      [data-testid="floating-view"] > div:first-child > div:first-child > div:first-child {
        min-width: 0;
        gap: 8px;
      }

      [data-testid="floating-view"] > div:first-child > div:first-child > div:first-child > span {
        min-width: 0;
        font-size: 16px;
        overflow-wrap: anywhere;
      }

      [data-testid="floating-view"] > div:first-child > div:first-child > div:last-child {
        flex-shrink: 0;
        gap: 0;
      }

      [data-testid="floating-view"] > div:first-child > div:first-child button {
        min-width: 44px;
        min-height: 44px;
        padding: 8px;
      }

      [data-testid="floating-view"] main {
        min-width: 0;
        min-height: 0;
      }

      [data-testid="floating-view"] textarea,
      [data-testid="floating-view"] input {
        max-width: 100%;
        font-size: 16px;
      }
    }

    /* Phones (same query as the newsletter tab, incl. phones held sideways):
       the launcher stays the bare circle. The vendor grows it into a pill
       with suggested questions and a label after a few seconds, which covers
       a big slice of a phone screen; everything that is not the avatar circle
       (or inside it) is hidden, so it never expands. Dragging and tapping are
       handled on the entrypoint itself and keep working. Desktop is untouched.
       Recheck the entrypoint / agent-avatar-bg selectors after a vendor update. */
    @media (max-width: 768.98px), (pointer: coarse) and (max-height: 540px) {
      [data-testid="entrypoint"] *:not(:has(.agent-avatar-bg)):not(.agent-avatar-bg):not(.agent-avatar-bg *) {
        display: none !important;
      }

      [data-testid="entrypoint"] [role="button"],
      [data-testid="entrypoint"] [role="button"] > * {
        padding: 0 !important;
        gap: 0 !important;
      }

    }

    /* Every screen: the launcher sits right above the language switcher
       (T-Lab, fixed 20px from the right and bottom; 46px tall on phones,
       35px on desktop), sharing its right edge with a 12px gap, and rises with
       it while the product buy panel is out (--rc-agent-lift, set on the host
       by sticky-atc-panel.css). left/top/margin/translate are reset because
       the vendor centres it on phones and puts it bottom left on desktop;
       dragging uses an inline transform and keeps working. */
    [data-testid="entrypoint"] {
      --rc-agent-bottom: 67px;
      position: fixed !important;
      top: auto !important;
      left: auto !important;
      right: 20px !important;
      bottom: calc(var(--rc-agent-bottom) + var(--rc-agent-lift, 0px)) !important;
      margin: 0 !important;
      translate: none !important;
    }

    @media (max-width: 768.98px) {
      [data-testid="entrypoint"] {
        --rc-agent-bottom: 78px;
      }
    }
  `;

  // The Bike Builder is a full-screen configurator: no agent there, on any
  // device (user request, 28/10). Hidden from inside its own shadow root so a
  // later rewrite of the host's inline style by the vendor cannot undo it.
  const hideOnThisPage = Boolean(document.querySelector('[data-bike-studio]'));
  const pageStyles = hideOnThisPage ? ':host { display: none !important; }' : '';

  let retryTimer;
  const observer = new MutationObserver(install);

  function install() {
    const host = document.getElementById(hostId);
    if (!host) return;

    const root = host.shadowRoot;
    if (!root) {
      if (!retryTimer) {
        retryTimer = window.setTimeout(() => {
          retryTimer = undefined;
          install();
        }, 250);
      }
      return;
    }

    if (!root.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = mobileStyles + pageStyles;
      root.append(style);
    }

    observer.disconnect();
    if (retryTimer) window.clearTimeout(retryTimer);

    // Follow the visible viewport when a mobile keyboard opens.
    const updateViewport = () => {
      const viewport = window.visualViewport;
      host.style.setProperty('--rc-clarity-viewport-height', `${viewport ? viewport.height : window.innerHeight}px`);
      host.style.setProperty('--rc-clarity-viewport-top', `${viewport ? viewport.offsetTop : 0}px`);
    };
    updateViewport();
    window.addEventListener('resize', updateViewport, { passive: true });
    window.visualViewport?.addEventListener('resize', updateViewport, { passive: true });
    window.visualViewport?.addEventListener('scroll', updateViewport, { passive: true });
  }

  observer.observe(document.body, { childList: true, subtree: true });
  install();
})();
